import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin, Sun, Thermometer, Wind, Droplets, AlertTriangle, RefreshCw, Info,
  ChevronDown, ChevronUp, CloudSun, ScanFace, Stethoscope, Beaker,
} from "lucide-react";

// ---------- scientific bands (WHO UV Index / US EPA AQI) ---------------------
const UV_BANDS = [
  { max: 2,  label: "Low",       color: "#4ade80", ring: "bg-emerald-400" },
  { max: 5,  label: "Moderate",  color: "#facc15", ring: "bg-yellow-400" },
  { max: 7,  label: "High",      color: "#fb923c", ring: "bg-orange-400" },
  { max: 10, label: "Very High", color: "#ef4444", ring: "bg-red-500" },
  { max: 100,label: "Extreme",   color: "#a855f7", ring: "bg-purple-500" },
];
const AQI_BANDS = [
  { max: 50,  label: "Good",           color: "#4ade80", ring: "bg-emerald-400" },
  { max: 100, label: "Moderate",       color: "#facc15", ring: "bg-yellow-400" },
  { max: 150, label: "Sensitive",      color: "#fb923c", ring: "bg-orange-400" },
  { max: 200, label: "Unhealthy",      color: "#ef4444", ring: "bg-red-500" },
  { max: 300, label: "Very Unhealthy", color: "#a855f7", ring: "bg-purple-500" },
  { max: 999, label: "Hazardous",      color: "#7f1d1d", ring: "bg-red-900" },
];
const HUM_BANDS = [
  { max: 30,  label: "Very Dry",  color: "#fb923c", ring: "bg-orange-400" },
  { max: 60,  label: "Balanced",  color: "#4ade80", ring: "bg-emerald-400" },
  { max: 80,  label: "Humid",     color: "#facc15", ring: "bg-yellow-400" },
  { max: 100, label: "Sweat-Zone",color: "#fb923c", ring: "bg-orange-400" },
];
const TEMP_BANDS = [
  { max: 12,  label: "Cool",  color: "#60a5fa", ring: "bg-sky-400" },
  { max: 28,  label: "Mild",  color: "#4ade80", ring: "bg-emerald-400" },
  { max: 34,  label: "Warm",  color: "#facc15", ring: "bg-yellow-400" },
  { max: 40,  label: "Hot",   color: "#fb923c", ring: "bg-orange-400" },
  { max: 100, label: "Extreme",color: "#ef4444",ring: "bg-red-500" },
];
const bandFor = (bands, v) => bands.find(b => v <= b.max) || bands[bands.length - 1];

// ---------- per-metric scientific advice + linked feature -------------------
const CTA_MAP = {
  sunguard:   { href: "/#sunscreen-coach",  label: "Check sunscreen",    icon: Sun },
  skinai:     { href: "/#skin-ai",          label: "Scan my skin",       icon: ScanFace },
  clinical:   { href: "/skin-analysis",     label: "Build a routine",    icon: Stethoscope },
};

function metricInsights({ uv, temp, humidity, aqi, pm25 }) {
  const out = [];
  // ---- UV -----------------------------------------------------------------
  const uvB = bandFor(UV_BANDS, uv);
  if (uv >= 8) {
    out.push({
      severity: "high", source: "UV",
      title: `Very high UV · ${uvB.label}`,
      reading: `WHO UV Index ${uv.toFixed(1)} · ~10 min to skin damage on Type III skin`,
      why: "UVB burns the top layer, UVA drives ageing & dark spots. At this level, damage begins before you feel it.",
      action: "Apply SPF 50 PA+++ before stepping out. Reapply every 2 hours. Cover ears, neck & nose bridge — SunGuard will spot missed patches.",
      cta: CTA_MAP.sunguard,
    });
  } else if (uv >= 6) {
    out.push({
      severity: "medium", source: "UV",
      title: `High UV · ${uvB.label}`,
      reading: `WHO UV Index ${uv.toFixed(1)}`,
      why: "Enough UV to cause tanning, spots and collagen loss in under 30 minutes of direct exposure.",
      action: "Broad-spectrum SPF 50 is essential today. Reapply every 3 hours if you're outdoors.",
      cta: CTA_MAP.sunguard,
    });
  } else if (uv >= 3) {
    out.push({
      severity: "low", source: "UV",
      title: `Moderate UV · ${uvB.label}`,
      reading: `WHO UV Index ${uv.toFixed(1)}`,
      why: "Not a burn risk in short bursts, but daily unprotected exposure adds up to pigmentation and fine lines over months.",
      action: "Wear a daily SPF 30–50 as your last morning step. A quick sunscreen coverage check is a good habit.",
      cta: CTA_MAP.sunguard,
    });
  } else {
    // UV low → route to Skin AI / Clinical (not SunGuard)
    out.push({
      severity: "info", source: "UV",
      title: `Low UV · ${uvB.label}`,
      reading: `WHO UV Index ${uv.toFixed(1)} · roughly early morning / cloudy / indoor level`,
      why: "Sunburn risk is minimal right now. This is a great window to run a skin check or start a new active without heavy sun stress.",
      action: "Use this calm sky to scan your skin's glow, tone and texture — Relumia's Skin AI reads six cosmetic signals in seconds.",
      cta: CTA_MAP.skinai,
    });
  }

  // ---- Humidity -----------------------------------------------------------
  if (humidity >= 80) {
    out.push({
      severity: "medium", source: "HUM",
      title: "High humidity",
      reading: `${humidity}% RH · sweat evaporates slowly`,
      why: "Sweat + sebum sit on the skin longer, thinning sunscreen and clogging pores — this is when acne, heat rash and 'melting' SPF happen.",
      action: "Reapply sunscreen after outdoor time or a workout. Use a lightweight, non-comedogenic moisturiser and keep body folds dry.",
      cta: CTA_MAP.sunguard,
    });
  } else if (humidity <= 30) {
    out.push({
      severity: "medium", source: "HUM",
      title: "Very dry air",
      reading: `${humidity}% RH · trans-epidermal water loss rises`,
      why: "Dry air pulls moisture out of the skin barrier, causing tightness, flaking and dullness.",
      action: "Add a hyaluronic-acid + ceramide moisturiser and drink water regularly. A guided routine helps you layer actives without irritating the barrier.",
      cta: CTA_MAP.clinical,
    });
  }

  // ---- AQI ----------------------------------------------------------------
  if (aqi > 150) {
    out.push({
      severity: "high", source: "AQI",
      title: "Unhealthy air",
      reading: `US EPA AQI ${aqi}${pm25 ? ` · PM₂.₅ ${pm25} µg/m³` : ""}`,
      why: "Fine particles settle on skin and generate free-radicals — over time they cause dullness, uneven tone and premature ageing.",
      action: "Double-cleanse in the evening. Antioxidant serums (Vitamin C) and oral glutathione help neutralise pollutant damage.",
      cta: CTA_MAP.skinai,
    });
  } else if (aqi > 100) {
    out.push({
      severity: "medium", source: "AQI",
      title: "Moderate pollution",
      reading: `US EPA AQI ${aqi}${pm25 ? ` · PM₂.₅ ${pm25} µg/m³` : ""}`,
      why: "Pollutants oxidise skin lipids and drive dullness over days–weeks, even before you see visible dirt.",
      action: "Cleanse thoroughly at night and consider a Vitamin-C or antioxidant serum in the morning.",
      cta: CTA_MAP.skinai,
    });
  }

  // ---- Temperature --------------------------------------------------------
  if (temp >= 35) {
    out.push({
      severity: "medium", source: "TEMP",
      title: "High heat",
      reading: `${temp.toFixed(0)}°C · blood flow to skin rises`,
      why: "Heat can trigger redness, prickly-heat and rosacea flares; barrier gets more reactive.",
      action: "Cool compress after outdoors, choose oil-control formulas, and let a clinician review your routine if flare-ups are frequent.",
      cta: CTA_MAP.clinical,
    });
  } else if (temp <= 12) {
    out.push({
      severity: "low", source: "TEMP",
      title: "Cool weather",
      reading: `${temp.toFixed(0)}°C · cold + dry indoor heating`,
      why: "Cold air is less humid; combined with indoor heaters it dehydrates the barrier fast. UV still causes photo-ageing on cool days.",
      action: "Layer an occlusive moisturiser at night and keep a light SPF for morning walks.",
      cta: CTA_MAP.clinical,
    });
  }

  return out;
}

// ---------- fetchers --------------------------------------------------------
async function fetchOpenMeteo(lat, lon) {
  const wxUrl = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,uv_index&daily=uv_index_max&timezone=auto`;
  const aqUrl = `https://air-quality-api.open-meteo.com/v1/air-quality?latitude=${lat}&longitude=${lon}&current=us_aqi,pm2_5,pm10&timezone=auto`;
  const [wx, aq] = await Promise.all([
    fetch(wxUrl).then(r => r.ok ? r.json() : {}),
    fetch(aqUrl).then(r => r.ok ? r.json() : {}),
  ]);
  const c = wx.current || {}, ac = aq.current || {}, d = wx.daily || {};
  return {
    uv: c.uv_index ?? (Array.isArray(d.uv_index_max) ? d.uv_index_max[0] : null),
    temp: c.temperature_2m ?? null,
    humidity: c.relative_humidity_2m ?? null,
    aqi: ac.us_aqi ?? null,
    pm25: ac.pm2_5 ?? null,
    pm10: ac.pm10 ?? null,
  };
}
async function fetchCity(lat, lon) {
  try {
    const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json&zoom=10&addressdetails=1`);
    if (!r.ok) return "";
    const j = await r.json();
    const a = j.address || {};
    const city = a.city || a.town || a.village || a.suburb || a.county || "";
    return [city, a.state || ""].filter(Boolean).join(", ");
  } catch { return ""; }
}
async function ipFallback() {
  const tryOne = async (fn) => { try { return await fn(); } catch { return null; } };
  const ipwho = await tryOne(async () => {
    const r = await fetch("https://ipwho.is/");
    if (!r.ok) return null;
    const j = await r.json();
    if (!j.success) return null;
    return { lat: j.latitude, lon: j.longitude, city: [j.city, j.region].filter(Boolean).join(", ") };
  });
  if (ipwho) return ipwho;
  return tryOne(async () => {
    const r = await fetch("https://ipinfo.io/json");
    if (!r.ok) return null;
    const j = await r.json();
    if (!j.loc) return null;
    const [lat, lon] = j.loc.split(",").map(Number);
    return isNaN(lat) ? null : { lat, lon, city: [j.city, j.region].filter(Boolean).join(", ") };
  });
}

// ---------- pieces ----------------------------------------------------------
const SEV_STYLE = {
  high:   { chip: "bg-red-100 text-red-800 border-red-300",     dot: "bg-red-500" },
  medium: { chip: "bg-amber-100 text-amber-800 border-amber-300", dot: "bg-amber-500" },
  low:    { chip: "bg-emerald-100 text-emerald-800 border-emerald-300", dot: "bg-emerald-500" },
  info:   { chip: "bg-sky-100 text-sky-800 border-sky-300",     dot: "bg-sky-500" },
};

function MetricPill({ icon: Icon, label, value, unit, band }) {
  return (
    <div className="flex items-center gap-2.5 min-w-0" data-testid={`strip-metric-${label.toLowerCase()}`}>
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-[color:var(--relumia-ink)]/10 bg-white/60">
        <Icon size={14} style={{ color: band?.color || "var(--relumia-gold-deep)" }} strokeWidth={2} />
      </div>
      <div className="min-w-0">
        <div className="flex items-baseline gap-1 leading-none">
          <span className="font-serif text-[18px] text-[color:var(--relumia-ink)]">{value ?? "—"}</span>
          {unit && <span className="text-[10px] text-[color:var(--relumia-muted)]">{unit}</span>}
        </div>
        <div className="flex items-center gap-1.5 mt-1">
          <span className="text-[9.5px] tracking-[0.15em] uppercase text-[color:var(--relumia-muted)] leading-none">{label}</span>
          {band && (
            <>
              <span className={`h-1 w-1 rounded-full ${band.ring}`} />
              <span className="text-[9.5px] tracking-wide leading-none" style={{ color: band.color }}>{band.label}</span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function SkinWeatherStrip() {
  const [data, setData] = useState(null);
  const [city, setCity] = useState("");
  const [open, setOpen] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [approx, setApprox] = useState(false);

  const run = useCallback(async (lat, lon, cityHint = "", approximate = false) => {
    const [wx, loc] = await Promise.all([
      fetchOpenMeteo(lat, lon),
      cityHint ? Promise.resolve(cityHint) : fetchCity(lat, lon),
    ]);
    setCity(loc || cityHint || "");
    setApprox(approximate);
    setData({
      uv: wx.uv != null ? Math.round(wx.uv * 10) / 10 : null,
      temp: wx.temp != null ? Math.round(wx.temp * 10) / 10 : null,
      humidity: wx.humidity != null ? Math.round(wx.humidity) : null,
      aqi: wx.aqi != null ? Math.round(wx.aqi) : null,
      pm25: wx.pm25 != null ? Math.round(wx.pm25 * 10) / 10 : null,
    });
  }, []);

  const load = useCallback(async () => {
    const ok = navigator.geolocation && await new Promise((res) =>
      navigator.geolocation.getCurrentPosition(
        (p) => { run(p.coords.latitude, p.coords.longitude); res(true); },
        () => res(false),
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      )
    );
    if (ok) return;
    const ip = await ipFallback();
    if (ip) run(ip.lat, ip.lon, ip.city, true);
  }, [run]);

  useEffect(() => { load(); }, [load]);

  if (!data) return null;

  const uv = data.uv ?? 0, temp = data.temp ?? 25, humidity = data.humidity ?? 50, aqi = data.aqi ?? 0, pm25 = data.pm25 ?? 0;
  const uvB = bandFor(UV_BANDS, uv);
  const aqB = bandFor(AQI_BANDS, aqi);
  const humB = bandFor(HUM_BANDS, humidity);
  const tempB = bandFor(TEMP_BANDS, temp);
  const insights = metricInsights({ uv, temp, humidity, aqi, pm25 });
  const primary = insights.find(a => a.severity === "high") || insights[0];

  return (
    <div
      data-testid="skin-weather-strip"
      className="absolute left-1/2 -translate-x-1/2 top-[74px] z-30 w-[calc(100%-2rem)] md:w-[min(96%,1240px)] pointer-events-none"
    >
      <motion.div
        layout
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1], delay: 0.4 }}
        className="pointer-events-auto relative rounded-2xl bg-white/85 backdrop-blur-2xl border border-[color:var(--relumia-gold)]/25 shadow-[0_18px_60px_-18px_rgba(60,45,20,0.35)] ring-1 ring-white/50 overflow-hidden"
      >
        {/* Gold accent stripe */}
        <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-gradient-to-b from-[color:var(--relumia-gold)] via-[color:var(--relumia-gold-deep)] to-[color:var(--relumia-gold)]" />

        {open ? (
          <>
            <div className="pl-5 md:pl-6 pr-3 md:pr-4 py-3 md:py-3.5">
              <div className="flex items-center gap-4 md:gap-6 flex-wrap md:flex-nowrap">
                {/* Live Nearby */}
                <div className="flex items-center gap-3 min-w-0 shrink-0">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-gradient-to-br from-[color:var(--relumia-gold)]/20 to-[color:var(--relumia-gold)]/5 border border-[color:var(--relumia-gold)]/40">
                    <CloudSun size={20} className="text-[color:var(--relumia-gold-deep)]" strokeWidth={1.7} />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[9.5px] tracking-[0.2em] uppercase text-[color:var(--relumia-gold-deep)] font-medium leading-none">Live Skin Weather</span>
                      {approx && <span className="text-[8.5px] tracking-widest text-amber-700 border border-amber-400/60 rounded-full px-1.5 py-[1px] leading-none" data-testid="strip-approx">APPROX</span>}
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      <MapPin size={11} className="text-[color:var(--relumia-gold-deep)] shrink-0" strokeWidth={2} />
                      <span className="text-[12.5px] text-[color:var(--relumia-ink)] truncate max-w-[200px]" data-testid="strip-location">{city || "Your area"}</span>
                    </div>
                  </div>
                </div>

                <div className="hidden md:block h-10 w-px bg-[color:var(--relumia-ink)]/10" />

                {/* Metrics — larger, banded */}
                <div className="grid grid-cols-2 md:flex md:items-center gap-x-5 gap-y-3 md:gap-5 flex-1 min-w-0">
                  <MetricPill icon={Sun}         label="UVI"      value={data.uv}       unit=""    band={uvB} />
                  <MetricPill icon={Thermometer} label="Temp"     value={data.temp}     unit="°C"  band={tempB} />
                  <MetricPill icon={Wind}        label="AQI"      value={data.aqi}      unit=""    band={aqB} />
                  <MetricPill icon={Droplets}    label="Humidity" value={data.humidity} unit="%"   band={humB} />
                </div>

                {/* Right cluster: primary alert + expand + refresh + collapse */}
                <div className="flex items-center gap-2 ml-auto shrink-0">
                  <button
                    onClick={() => setExpanded((v) => !v)}
                    data-testid="strip-expand-btn"
                    className={`hidden md:inline-flex items-center gap-2 rounded-full px-4 py-2 text-[11.5px] tracking-wide font-medium transition-colors ${
                      primary?.severity === "high"
                        ? "bg-red-600 text-white hover:bg-red-700"
                        : primary?.severity === "medium"
                        ? "bg-[color:var(--relumia-gold)] text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-gold-deep)] hover:text-white"
                        : "bg-[color:var(--relumia-ink)] text-white hover:bg-[color:var(--relumia-gold-deep)]"
                    }`}
                  >
                    <AlertTriangle size={13} strokeWidth={2} />
                    {insights.length} skin alert{insights.length !== 1 ? "s" : ""}
                    {expanded ? <ChevronUp size={13} strokeWidth={2} /> : <ChevronDown size={13} strokeWidth={2} />}
                  </button>
                  <button
                    onClick={() => setShowInfo((v) => !v)}
                    data-testid="strip-info"
                    aria-label="Data sources"
                    className="p-2 rounded-full text-[color:var(--relumia-muted)] hover:text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-ink)]/5 transition-colors"
                  >
                    <Info size={14} strokeWidth={1.8} />
                  </button>
                  <button
                    onClick={load}
                    data-testid="strip-refresh"
                    aria-label="Refresh"
                    className="p-2 rounded-full text-[color:var(--relumia-muted)] hover:text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-ink)]/5 transition-colors"
                  >
                    <RefreshCw size={14} strokeWidth={1.8} />
                  </button>
                  <button
                    onClick={() => setOpen(false)}
                    data-testid="strip-collapse"
                    aria-label="Hide"
                    className="p-2 rounded-full text-[color:var(--relumia-muted)] hover:text-[color:var(--relumia-ink)] hover:bg-[color:var(--relumia-ink)]/5 transition-colors"
                  >
                    <ChevronUp size={15} strokeWidth={1.9} />
                  </button>
                </div>
              </div>

              {/* Mobile expand button */}
              <button
                onClick={() => setExpanded((v) => !v)}
                data-testid="strip-expand-mobile"
                className="md:hidden mt-3 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[color:var(--relumia-gold)] text-[color:var(--relumia-ink)] px-4 py-2 text-[12px] tracking-wide font-medium"
              >
                <AlertTriangle size={13} strokeWidth={2} />
                {insights.length} skin alert{insights.length !== 1 ? "s" : ""} · tap for advice
                {expanded ? <ChevronUp size={13} strokeWidth={2} /> : <ChevronDown size={13} strokeWidth={2} />}
              </button>
            </div>

            {/* Expanded advice panel */}
            <AnimatePresence>
              {expanded && (
                <motion.div
                  key="adv"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
                  className="overflow-hidden border-t border-[color:var(--relumia-ink)]/10 bg-white/60"
                  data-testid="strip-panel"
                >
                  <div className="px-5 md:px-6 py-4 space-y-2.5">
                    <div className="flex items-center gap-2 mb-2">
                      <Beaker size={13} className="text-[color:var(--relumia-gold-deep)]" strokeWidth={2} />
                      <span className="text-[10px] tracking-[0.2em] uppercase text-[color:var(--relumia-gold-deep)] font-medium">Dermatology-friendly guidance</span>
                    </div>
                    {insights.map((a, i) => {
                      const s = SEV_STYLE[a.severity] || SEV_STYLE.info;
                      const CtaIcon = a.cta.icon;
                      const cta = a.cta;
                      const CtaEl = cta.href.startsWith("/#") ? "a" : Link;
                      const ctaProps = cta.href.startsWith("/#") ? { href: cta.href } : { to: cta.href };
                      return (
                        <div key={i} data-testid={`strip-insight-${i}`} className="rounded-md border border-[color:var(--relumia-ink)]/8 bg-white/70 p-4">
                          <div className="flex items-start justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-2 min-w-0">
                              <span className={`h-2 w-2 rounded-full ${s.dot} shrink-0`} />
                              <span className={`text-[9.5px] tracking-[0.18em] uppercase font-medium px-2 py-0.5 rounded-full border ${s.chip}`}>{a.source}</span>
                              <span className="text-[13px] text-[color:var(--relumia-ink)] font-medium">{a.title}</span>
                            </div>
                            <CtaEl
                              {...ctaProps}
                              data-testid={`strip-insight-cta-${i}`}
                              className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--relumia-ink)] text-white px-3.5 py-1.5 text-[11px] tracking-wide hover:bg-[color:var(--relumia-gold-deep)] transition-colors whitespace-nowrap"
                            >
                              <CtaIcon size={12} strokeWidth={2} />
                              {cta.label}
                            </CtaEl>
                          </div>
                          <div className="mt-2 text-[10.5px] tracking-wide uppercase text-[color:var(--relumia-gold-deep)]/85">{a.reading}</div>
                          <div className="mt-2 grid md:grid-cols-2 gap-2 md:gap-4">
                            <div>
                              <div className="text-[10px] tracking-[0.15em] uppercase text-[color:var(--relumia-muted)] mb-1">Why it matters</div>
                              <p className="text-[12px] leading-relaxed text-[color:var(--relumia-ink)]/80">{a.why}</p>
                            </div>
                            <div>
                              <div className="text-[10px] tracking-[0.15em] uppercase text-[color:var(--relumia-muted)] mb-1">What to do</div>
                              <p className="text-[12px] leading-relaxed text-[color:var(--relumia-ink)]/80">{a.action}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div className="pt-2 text-[10px] text-[color:var(--relumia-muted)] leading-relaxed">
                      Standards: WHO UV Index · US EPA AQI · dermatology-aligned guidance. Not a medical diagnosis.
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </>
        ) : (
          <button
            onClick={() => setOpen(true)}
            data-testid="strip-open"
            aria-label="Show Live Skin Weather"
            className="flex items-center gap-2 pl-4 pr-4 py-2.5 text-[11.5px] tracking-wide text-[color:var(--relumia-ink)]"
          >
            <CloudSun size={14} className="text-[color:var(--relumia-gold-deep)]" strokeWidth={1.9} />
            <span>Live Skin Weather</span>
            <ChevronDown size={13} strokeWidth={1.9} />
          </button>
        )}

        {/* Info popover */}
        {showInfo && open && (
          <div data-testid="strip-info-panel"
            className="absolute right-4 top-full mt-2 w-[300px] rounded-sm bg-white/95 backdrop-blur-xl border border-[color:var(--relumia-champagne)] shadow-lg p-4 text-[11.5px] leading-relaxed text-[color:var(--relumia-ink)]/85 z-10">
            Location stays in your browser only — nothing is stored. Weather &amp; UV Index:{" "}
            <a href="https://open-meteo.com" target="_blank" rel="noreferrer" className="text-gold underline">Open-Meteo</a>.
            Air quality: US EPA AQI via Open-Meteo. Location naming:{" "}
            <a href="https://openstreetmap.org" target="_blank" rel="noreferrer" className="text-gold underline">OpenStreetMap</a>.
          </div>
        )}
      </motion.div>
    </div>
  );
}
